<template>
  <button
    :class="['color-swatch', { selected, disabled, 'has-image': hasImage }]"
    :disabled="disabled"
    :title="label"
    :aria-label="`Sélectionner la couleur ${label}`"
    @click="handleClick"
  >
    <div class="swatch-color" :style="swatchStyle">
      <div v-if="isBicolor" class="swatch-bicolor">
        <img class="swatch-half swatch-half-top" :src="getColorImageUrl(primaryColorCode)" :alt="primaryColor" />
        <img class="swatch-half swatch-half-bottom" :src="getColorImageUrl(secondaryColorCode)" :alt="secondaryColor" />
      </div>
      <img v-else-if="primaryColor" class="swatch-simple" :src="getColorImageUrl(primaryColorCode)" :alt="primaryColor" />
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

// Props
interface Props {
  color: string;
  label: string;
  selected?: boolean;
  disabled?: boolean;
  imageUrl?: string;
}

const props = withDefaults(defineProps<Props>(), {
  selected: false,
  disabled: false,
  imageUrl: undefined
});

// Emits
const emit = defineEmits<{
  click: [];
}>();

// Utils
/**
 * Extrait le code couleur du chemin complet
 * Ex: "EXT-GRD_P10GL-BG_A-BC_WA1" → "WA1"
 */
function extractColorCode(colorPath: string): string {
  const match = colorPath.match(/BC_([A-Z0-9]+)$/);
  return match ? match[1] : colorPath;
}

/**
 * Génère l'URL de l'image de couleur avec le BASE_URL
 */
function getColorImageUrl(colorCode: string): string {
  return `${import.meta.env.BASE_URL}teintes/BC_${colorCode}.png`;
}

// Computed
const isBicolor = computed(() => {
  // Détecte les couleurs bicolores (contiennent 'x' ou sont des IDs spéciaux)
  return props.color.includes(' x ') || props.color.includes(' X ') || props.label.includes(' x ') || props.label.includes(' X ');
});

const primaryColor = computed(() => {
  if (isBicolor.value) {
    // Pour les bicolores, prendre la première partie avant 'x'
    const parts = props.color.split(/\s*x\s*/i);
    return parts[0] || '';
  }
  // Pour les couleurs simples, retourner le code tel quel (sera utilisé pour le chemin d'image)
  return props.color.startsWith('#') ? '' : props.color;
});

/**
 * Extrait le code couleur pour l'image à partir du chemin complet
 */
const primaryColorCode = computed(() => extractColorCode(primaryColor.value));

const secondaryColor = computed(() => {
  if (isBicolor.value) {
    // Pour les bicolores, prendre la deuxième partie après 'x'
    const parts = props.color.split(/\s*x\s*/i);
    return parts[1] || '';
  }
  return '';
});

/**
 * Extrait le code couleur secondaire pour l'image à partir du chemin complet
 */
const secondaryColorCode = computed(() => extractColorCode(secondaryColor.value));

const swatchStyle = computed(() => {
  // Priorité 1: imageUrl externe
  if (props.imageUrl) {
    return { backgroundImage: `url(${props.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' };
  }

  if (isBicolor.value) {
    return {};
  }

  // Pour les couleurs simples
  if (props.color.startsWith('#')) {
    return { backgroundColor: props.color };
  } else if (props.color.startsWith('http') || props.color.includes('/')) {
    return { backgroundImage: `url(${props.color})`, backgroundSize: 'cover', backgroundPosition: 'center' };
  } else {
    // Fallback pour les couleurs non reconnues
    return { backgroundColor: '#cccccc' };
  }
});

/**
 * Détecte si le swatch utilise une image (via imageUrl, URL dans color, ou code couleur teintes)
 */
const hasImage = computed(() => !!props.imageUrl || props.color.startsWith('http') || props.color.includes('/') || (!props.color.startsWith('#') && props.color.length > 0));

// Methods
const handleClick = (): void => {
  if (!props.disabled) {
    emit('click');
  }
};
</script>

<style scoped>
.color-swatch {
  position: relative;
  border: 1px solid transparent;
  box-shadow: none;
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  overflow: hidden;
  width: 110px;
  height: 48px;
}

.color-swatch:hover:not(.disabled) {
  border-color: var(--color-border-light);
}

.color-swatch.selected {
  border-color: var(--color-border-light);
}

.color-swatch.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.color-swatch:focus {
  outline: none;
  border-color: var(--color-border-light);
}

.swatch-color {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.color-swatch.has-image .swatch-color {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* Pour les images PNG avec transparence */
.color-swatch.has-image {
  background: transparent;
}

.color-swatch.has-image:hover:not(.disabled) {
  border-color: var(--color-border-light);
}

.swatch-bicolor {
  width: 100%;
  height: 100%;
  position: relative;
}

.swatch-half {
  position: absolute;
  width: 100%;
  height: 50%;
  clip-path: polygon(0 0, 100% 0, 50% 100%);
}

.swatch-simple {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.swatch-half-top {
  top: 0;
  clip-path: polygon(0 0, 100% 0, 50% 100%);
}

.swatch-half-bottom {
  bottom: 0;
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
}


</style>
